import { IconSelectClean } from "@components/inputs/select/IconSelect";
import { ISelectItem, TSelectItemId } from "@components/inputs/select/Select.types";
import { IFieldInfoProperties, IGetValueArgs, TGetValueFn, TTemporalDialogSettings } from "@components/smart/FieldInfo";
import { createBindingContext, IEntity } from "@odata/BindingContext";
import {
    EntitySetName,
    EntityTypeName,
    IPrEmploymentEntity,
    IPrEmploymentSalaryComponentEntity,
    IPrSalaryComponentEntity,
    IPrSalaryComponentTemporalCurrentEntity,
    PrEmploymentEntity,
    PrEmploymentSalaryComponentTemporalEntity,
    PrEmploymentTemplateEntity,
    PrEmploymentTemplateSalaryComponentEntity,
    PrEmploymentTemplateSalaryComponentTemporalEntity,
    PrFixedBonusPayOutImpactEntity,
    PrFixedBonusTaxImpactEntity,
    PrRegularSalaryTypeEntity,
    PrSalaryComponentEntity,
    PrSalaryComponentTemporalEntity,
    PrSalaryComponentTypeEntity
} from "@odata/GeneratedEntityTypes";
import {
    PrEntityValueSourceCode,
    PrFixedBonusPayOutImpactCode,
    PrFixedBonusTaxImpactCode,
    PrRegularSalaryTypeCode,
    PrSalaryComponentTypeCode
} from "@odata/GeneratedEnums";
import { getEnumNameSpaceName, getEnumSelectItems, getEnumValues } from "@odata/GeneratedEnums.utils";
import { ODataQueryBuilder, Query } from "@odata/OData";
import {
    getTemporalPropertyLineItemFieldDefinition,
    IGetTemporalPropertyLineItemFieldDefinitionArgs,
    isFieldSynchronized,
    replaceSyncedValueWithHistoryValues,
    TemporalEntityProp,
    TTemporal
} from "@odata/TemporalUtils";
import { TFieldsDefinition } from "@pages/PageUtils";
import { getCompanyCurrency } from "@utils/CompanyUtils";
import i18next from "i18next";

import { BasicInputSizes, FieldType, GroupedField, QueryParam, ToolbarItemType, ValidatorType } from "../../../enums";
import { TableStorage } from "../../../model/TableStorage";
import { getQueryParameters } from "../../../routes/Routes.utils";
import CurrencyType from "../../../types/Currency";
import { DATE_MIN } from "../../../types/Date";
import { FormStorage } from "../../../views/formView/FormStorage";
import { ICustomButtonDefArgs, TableActionOrder, TTableToolbarItem } from "../../../views/table/TableToolbar.utils";

export const ADD_SALARY_COMPONENT_BUTTON = "SalaryComponentAddButton";

export function getSalaryComponentTypesForForm(): PrSalaryComponentTypeCode[] {
    return getEnumValues<PrSalaryComponentTypeCode>(EntityTypeName.PrSalaryComponentType)
        .filter(type => type !== PrSalaryComponentTypeCode.BonusTimebased);
}

export const SalaryEnums = [
    EntityTypeName.PrSalaryComponentType,
    EntityTypeName.PrRegularSalaryType,
    EntityTypeName.PrFixedBonusTaxImpact,
    EntityTypeName.PrFixedBonusPayOutImpact,
    EntityTypeName.PrIncomeType,
    EntityTypeName.CsszEmploymentType
];

export const getSalaryEnumNamespaceNames = (): string[] => SalaryEnums.map(getEnumNameSpaceName);

/**
 * Configuration of add button icon select with all salary component types
 * @param storage
 * @param ref
 * @param args
 */
export function getToolbarAddButtonConfig(storage: TableStorage, ref: React.RefObject<IconSelectClean>, args?: ICustomButtonDefArgs): TTableToolbarItem {
    const supportedTypes = getSalaryComponentTypesForForm();
    const items = getEnumSelectItems(EntityTypeName.PrSalaryComponentType)
        .filter(item => supportedTypes.includes(item.id as PrSalaryComponentTypeCode));

    return {
        id: ADD_SALARY_COMPONENT_BUTTON,
        itemType: ToolbarItemType.IconSelect,
        iconName: "Add",
        label: i18next.t("SalaryComponent:Table.Add"),
        order: TableActionOrder.Add,
        itemProps: {
            isTransparent: true,
            selectProps: {
                isActive: storage.data.addingRow || args?.isActive,
                isDisabled: !!storage.tableAction || args?.isDisabled,
                ref
            },
            items: [...items]
        }
    };
}

export const areAllSalaryComponentsOfSameType = (args: IGetValueArgs): boolean => {
    const entity = args.storage.data.entity as IPrEmploymentEntity;
    const salaryComponents = entity.SalaryComponents ?? [];
    if (salaryComponents.length < 2) {
        return true;
    }
    const refType = salaryComponents[0].CurrentTemporalPropertyBag?.Type?.Code;
    return !salaryComponents.slice(1).find(sc => sc.CurrentTemporalPropertyBag?.Type?.Code !== refType);
}

export function isSalaryComponentType(...types: PrSalaryComponentTypeCode[]): TGetValueFn<boolean> {
    return (args: IGetValueArgs) => {
        const { storage, bindingContext } = args;
        // used in different contexts (employment, employment template, salary component), but parentBc should be always the salary component
        let salaryComponentBagBc = bindingContext.getParent();
        if (salaryComponentBagBc.getPath(true) === TemporalEntityProp.TemporalPropertyBag) {
            // in temporal dialog, SmartFastEntryList use temporal property bag Bc,
            // but we need to check only current value
            salaryComponentBagBc = salaryComponentBagBc.getParent().navigate(TemporalEntityProp.CurrentTemporalPropertyBag);
        }
        const salaryComponentBc = salaryComponentBagBc.getParent();
        if (salaryComponentBc.isCollection() && !salaryComponentBc.getKey()) {
            const refType = args.storage.data.entity?.SalaryComponents?.[0].CurrentTemporalPropertyBag?.Type?.Code;
            return types.includes(refType as PrSalaryComponentTypeCode);
        }
        const component = storage.getValue(salaryComponentBagBc) as IPrSalaryComponentTemporalCurrentEntity;
        return !component || types.includes(component?.Type?.Code as PrSalaryComponentTypeCode);
    };
}

export enum SalaryComponentVariant {
    Base = "Base",
    Employment = "Employment",
    EmploymentTemplate = "EmploymentTemplate"
}

export function getSalaryComponentColumns(type: SalaryComponentVariant = SalaryComponentVariant.Base): string[] {
    const SalaryComponentEntity = type === SalaryComponentVariant.Employment ? PrEmploymentSalaryComponentTemporalEntity
        : type === SalaryComponentVariant.EmploymentTemplate ? PrEmploymentTemplateSalaryComponentTemporalEntity : PrSalaryComponentTemporalEntity;

    return [
        SalaryComponentEntity.Name,
        SalaryComponentEntity.Type,
        SalaryComponentEntity.RegularSalaryType,
        SalaryComponentEntity.DefaultPercentage,
        SalaryComponentEntity.FixedBonusTaxImpact,
        SalaryComponentEntity.FixedBonusPayOutImpact,
        SalaryComponentEntity.Amount
    ];
}

// needs to be in callback, so that translation is loaded
export const getSalaryComponentTemporalDialogSettings = (type: SalaryComponentVariant = SalaryComponentVariant.Base, withHistory = true): TTemporalDialogSettings => {
    return {
        dialogTitle: i18next.t("SalaryComponent:Form.PlanSalaryComponentTitle"),
        columns: getSalaryComponentColumns(type),
        extractHistory: withHistory ? getSalaryComponentItemHistory : undefined,
        newDefaultDateValidFrom: DATE_MIN
    };
};

export function getSalaryComponentFieldDefinition(propName: string, temporalDialogSettings?: TTemporalDialogSettings): Partial<IFieldInfoProperties> {
    let def: Partial<IFieldInfoProperties> = {};
    switch(propName) {
        case PrSalaryComponentTemporalEntity.Name:
            def = {
                width: BasicInputSizes.L,
            };
            break;
        case PrSalaryComponentTemporalEntity.Type:
            def = {
                width: BasicInputSizes.M,
                type: FieldType.ComboBox,
                defaultValue: (args: IGetValueArgs) => {
                    return getQueryParameters()[QueryParam.Type] ?? PrSalaryComponentTypeCode.Regular;
                },
                fieldSettings: {
                    displayName: PrSalaryComponentTypeEntity.Name,
                }
                // isRequired: isSalaryComponentWithoutDefault
            };
            break;
        case PrSalaryComponentTemporalEntity.RegularSalaryType:
            def = {
                width: BasicInputSizes.M,
                type: FieldType.ComboBox,
                defaultValue: PrRegularSalaryTypeCode.Monthly,
                fieldSettings: {
                    displayName: PrRegularSalaryTypeEntity.Name
                },
                isVisible: isSalaryComponentType(PrSalaryComponentTypeCode.Regular)
            };
            break;
        case PrSalaryComponentTemporalEntity.FixedBonusTaxImpact:
            def = {
                width: BasicInputSizes.M,
                type: FieldType.ComboBox,
                defaultValue: PrFixedBonusTaxImpactCode.Full,
                fieldSettings: {
                    displayName: PrFixedBonusTaxImpactEntity.Name
                },
                isVisible: isSalaryComponentType(PrSalaryComponentTypeCode.Fixed)
            };
            break;
        case PrSalaryComponentTemporalEntity.FixedBonusPayOutImpact:
            def = {
                width: BasicInputSizes.M,
                type: FieldType.ComboBox,
                defaultValue: PrFixedBonusPayOutImpactCode.ToPayOut,
                fieldSettings: {
                    displayName: PrFixedBonusPayOutImpactEntity.Name,
                    itemsFactory: async (args: IGetValueArgs): Promise<ISelectItem<TSelectItemId>[]> => {
                        return getEnumSelectItems(EntityTypeName.PrFixedBonusPayOutImpact).map(item => ({
                            ...item,
                            label: i18next.t("SalaryComponent:FixedBonusPayOutImpact." + item.id)
                        }));
                    }
                },
                isVisible: isSalaryComponentType(PrSalaryComponentTypeCode.Fixed)
            };
            break;
        case PrSalaryComponentTemporalEntity.DefaultPercentage:
            def = {
                width: BasicInputSizes.S,
                type: FieldType.NumberInput,
                defaultValue: 100,
                fieldSettings: {
                    unit: "%",
                    showSteppers: true,
                    min: 0,
                    max: 100
                },
                validator: {
                    type: ValidatorType.Number,
                    settings: {
                        min: 0,
                        max: 100
                    }
                },
                isVisible: isSalaryComponentType(PrSalaryComponentTypeCode.BonusPercentage)
            };
            break;
        case PrSalaryComponentTemporalEntity.Amount:
            def = {
                width: BasicInputSizes.S,
                fieldSettings: {
                    unit: args => CurrencyType.getCurrencyUnit(getCompanyCurrency(args.context))
                },
            };
            break;
    }

    return {
        ...def,
        fieldSettings: {
            ...def.fieldSettings,
            temporalDialog: temporalDialogSettings
        }
    };
}

export function getSalaryComponentFieldDefinitions(type: SalaryComponentVariant = SalaryComponentVariant.Base): TFieldsDefinition {
    const isEmployment = type === SalaryComponentVariant.Employment;
    const isTemplate = type === SalaryComponentVariant.EmploymentTemplate;
    const isBase = type === SalaryComponentVariant.Base;

    const MainEntity = isTemplate ? PrEmploymentTemplateEntity : (isEmployment ? PrEmploymentEntity : null);
    const SalaryComponentEntity = isEmployment ? PrEmploymentSalaryComponentTemporalEntity
        : isTemplate ? PrEmploymentTemplateSalaryComponentTemporalEntity : PrSalaryComponentTemporalEntity;

    const collectionName = MainEntity ? MainEntity.SalaryComponents : null;
    const isSynchronized = !!collectionName;

    const temporalDialogSettings: TTemporalDialogSettings = getSalaryComponentTemporalDialogSettings(type, !isBase);

    const commonDefs: Omit<IGetTemporalPropertyLineItemFieldDefinitionArgs, "propName"> = {
        collectionName, withOpeningButton: false, isSynchronized
    };

    const columns = getSalaryComponentColumns(type);

    // additional properties, which are attached to the amount field
    const additionalProperties = isBase ? undefined
        : columns.map(id => ({ id: `/${MainEntity.SalaryComponents}/${PrSalaryComponentEntity.TemporalPropertyBag}/${id}` }));

    const isExistingSalaryComponentLineItem = (args: IGetValueArgs) => {
        return !args.bindingContext.getParent().getParent().isNew();
    };

    return {
        ...getTemporalPropertyLineItemFieldDefinition({
            ...commonDefs,
            propName: SalaryComponentEntity.Name,
            definition: {
                ...getSalaryComponentFieldDefinition(SalaryComponentEntity.Name, temporalDialogSettings),
                groupedField: GroupedField.WrappedMultiStart
            }
        }),
        ...getTemporalPropertyLineItemFieldDefinition({
            ...commonDefs,
            propName: SalaryComponentEntity.Type,
            definition: {
                ...getSalaryComponentFieldDefinition(SalaryComponentEntity.Type, temporalDialogSettings),
                isReadOnly: true,
            },
            currentDefinition: {
                isReadOnly: isBase ? true : (args: IGetValueArgs) => isExistingSalaryComponentLineItem(args) || isFieldSynchronized(args)
            }
        }),
        ...getTemporalPropertyLineItemFieldDefinition({
            ...commonDefs,
            propName: SalaryComponentEntity.RegularSalaryType,
            definition: {
                ...getSalaryComponentFieldDefinition(SalaryComponentEntity.RegularSalaryType, temporalDialogSettings),
            }
        }),
        ...getTemporalPropertyLineItemFieldDefinition({
            ...commonDefs,
            propName: SalaryComponentEntity.FixedBonusTaxImpact,
            definition: {
                ...getSalaryComponentFieldDefinition(SalaryComponentEntity.FixedBonusTaxImpact, temporalDialogSettings),
            }
        }),
        ...getTemporalPropertyLineItemFieldDefinition({
            ...commonDefs,
            propName: SalaryComponentEntity.FixedBonusPayOutImpact,
            definition: {
                ...getSalaryComponentFieldDefinition(SalaryComponentEntity.FixedBonusPayOutImpact, temporalDialogSettings),
            }
        }),
        ...getTemporalPropertyLineItemFieldDefinition({
            ...commonDefs,
            propName: SalaryComponentEntity.DefaultPercentage,
            definition: {
                ...getSalaryComponentFieldDefinition(SalaryComponentEntity.DefaultPercentage, temporalDialogSettings),
            }
        }),
        ...getTemporalPropertyLineItemFieldDefinition({
            ...commonDefs,
            withOpeningButton: true,
            propName: SalaryComponentEntity.Amount,
            definition: {
                ...getSalaryComponentFieldDefinition(SalaryComponentEntity.Amount, temporalDialogSettings),
                groupedField: GroupedField.WrappedMultiEnd,
                additionalProperties
            }
        })
    };
}


const expandSalaryComponent = <T extends Query = Query>(query: T): T =>
    query.expand(PrSalaryComponentEntity.TemporalPropertyBag, q =>
        q.expand(PrSalaryComponentTemporalEntity.Type));

const expandTemplateSalaryComponent = <T extends Query = Query>(query: T): T =>
    expandSalaryComponent(query)
        .expand(PrEmploymentTemplateSalaryComponentEntity.Default, q => expandSalaryComponent(q));

async function getSalaryComponentItemHistory(storage: FormStorage, component: IEntity, data: TTemporal[]): Promise<TTemporal[]> {
    if (!data) {
        data = [];
    }

    const baseEntity = component.Template ?? component.Default;
    const hasTemplate = !!component.Template;

    if (!baseEntity) {
        // there are no default data, just return actual TemporalPropertyBag
        return data;
    }

    const { oData } = storage;

    let historyData: TTemporal[];

    let query: ODataQueryBuilder;
    if (hasTemplate) {
        const template = storage.data.entity.Template;
        const bc = createBindingContext(EntitySetName.PrEmploymentTemplates, oData.metadata).addKey(template)
            .navigate(PrEmploymentTemplateEntity.SalaryComponents).addKey(baseEntity);
        query = oData.fromPath(bc.getFullPath()).query();
        query = expandTemplateSalaryComponent(query);

        const result = await query.fetchData<IPrEmploymentSalaryComponentEntity>();
        const salaryComp = result?.value;
        // extract additional NULL values from template from the Default
        historyData = (salaryComp?.TemporalPropertyBag ?? [])
            .reduce((res, current) =>
                res.concat(...replaceSyncedValueWithHistoryValues(current, salaryComp.Default.TemporalPropertyBag, PrEntityValueSourceCode.TemplateDefault)), []);
    } else {
        query = oData.getEntitySetWrapper(EntitySetName.PrSalaryComponents).query(baseEntity.Id);
        query = expandSalaryComponent(query);

        const result = await query.fetchData<IPrSalaryComponentEntity>();
        historyData = result.value.TemporalPropertyBag as TTemporal[];
    }

    return data.reduce((res: TTemporal[], item: TTemporal) =>
        res.concat(replaceSyncedValueWithHistoryValues(item, historyData, hasTemplate ? PrEntityValueSourceCode.Template : PrEntityValueSourceCode.Default)), []);
}